import React, { useState } from 'react';
import { Bell, Search, Settings, LogOut, User, ChevronDown, Command } from 'lucide-react';
import { useAuthStore } from '../../store/auth.store';
import { useNavigate } from 'react-router-dom';

export function TopBar() {
  const { user, logout } = useAuthStore();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || user.email[0].toUpperCase()
    : 'U';

  const roleBadge: Record<string, { label: string; color: string }> = {
    super_admin: { label: 'Super Admin', color: 'text-red-300 bg-red-500/20' },
    tenant_admin: { label: 'Admin',       color: 'text-orange-300 bg-orange-500/20' },
    operator:    { label: 'Operator',    color: 'text-blue-300 bg-blue-500/20' },
    developer:   { label: 'Developer',   color: 'text-purple-300 bg-purple-500/20' },
    viewer:      { label: 'Viewer',      color: 'text-green-300 bg-green-500/20' },
  };

  const role = user?.role ? roleBadge[user.role] : null;

  return (
    <header className="h-14 bg-[#3a3a3a] border-b border-black/20 flex items-center px-6 gap-4 shadow-sm">
      {/* Search */}
      <div className="flex-1 max-w-md">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            type="text"
            placeholder="Search workflows, blueprints... (⌘K)"
            className="w-full bg-white/10 border border-white/15 rounded-lg pl-9 pr-4 py-2 text-sm text-white/80 placeholder-white/35 focus:outline-none focus:border-white/40 focus:bg-white/15 transition-all"
          />
          <kbd className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 px-1.5 py-0.5 bg-white/10 rounded text-[10px] text-white/35 font-mono">
            <Command className="w-2.5 h-2.5" />K
          </kbd>
        </div>
      </div>

      <div className="flex-1" />

      {/* Actions */}
      <div className="flex items-center gap-2">
        {/* Notifications */}
        <button className="relative p-2 rounded-lg text-white/50 hover:text-white hover:bg-white/10 transition-colors">
          <Bell className="w-4 h-4" />
          <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-orbayx-400" />
        </button>

        {/* User Menu */}
        <div className="relative">
          <button
            onClick={() => setShowUserMenu(!showUserMenu)}
            className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-lg hover:bg-white/10 transition-colors"
          >
            <div className="w-7 h-7 rounded-lg bg-orbayx-500 flex items-center justify-center text-xs font-bold text-white">
              {initials}
            </div>
            <div className="text-left hidden sm:block">
              <div className="text-xs font-medium text-white/85">
                {user?.firstName ? `${user.firstName} ${user.lastName || ''}`.trim() : user?.email}
              </div>
              {role && (
                <div className={`text-[10px] px-1 rounded ${role.color}`}>{role.label}</div>
              )}
            </div>
            <ChevronDown className="w-3 h-3 text-white/40" />
          </button>

          {showUserMenu && (
            <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-xl shadow-lg z-50 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="text-sm font-medium text-gray-800">{user?.email}</div>
                {role && <div className={`text-xs mt-0.5 text-orbayx-600`}>{role.label}</div>}
              </div>
              <div className="p-1">
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings/profile'); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" /> Profile
                </button>
                <button
                  onClick={() => { setShowUserMenu(false); navigate('/settings'); }}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" /> Settings
                </button>
              </div>
              <div className="p-1 border-t border-gray-100">
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 w-full px-3 py-2 text-sm text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <LogOut className="w-4 h-4" /> Sign out
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
